import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import PageShell from "../dashboard/PageShell";
import { Plus, Search, FileDown, Pencil, Trash2 } from "lucide-react";

interface StandardItem {
  id: string;
  stt: number;
  standardName: string;
  unit: string;
  scienceTask: string;
  symbol: string;
  issuingAuthority: string;
  time: string;
  category: string;
}

const mockData: StandardItem[] = [
  {
    id: "1",
    stt: 1,
    standardName: "A",
    unit: "A",
    scienceTask: "A",
    symbol: "A",
    issuingAuthority: "Bộ Công an",
    time: "18/11/2022",
    category: "Tiêu chuẩn quốc gia TCVN/QS",
  },
  {
    id: "2",
    stt: 1,
    standardName: "B",
    unit: "C",
    scienceTask: "C",
    symbol: "C",
    issuingAuthority: "Chính Phủ",
    time: "18/11/2022",
    category: "Tiêu chuẩn cơ sở TCQS",
  },
];

const Standards: React.FC = () => {
  const navigate = useNavigate();
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [standardToDelete, setStandardToDelete] = useState<StandardItem | null>(
    null
  );

  // Group data by category
  const groupedData = mockData.reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = [];
    }
    acc[item.category].push(item);
    return acc;
  }, {} as Record<string, StandardItem[]>);

  return (
    <PageShell title="Danh sách tiêu chuẩn">
      <div className="space-y-6">
        {/* Header Section */}
        <div className="flex justify-between items-center">
          <h1 className="text-xl font-semibold text-gray-800">
            Danh sách tiêu chuẩn
          </h1>
          <button
            onClick={() => navigate("/add-new-standard")}
            className="bg-green-600 text-white px-3 py-2 rounded text-sm font-medium hover:bg-green-700 transition-colors flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>Thêm mới Tiêu chuẩn</span>
          </button>
        </div>

        {/* Search and Filter Section */}
        <div className="bg-white rounded-lg p-4">
          <div className="space-y-4">
            {/* Row 1 - Search input + Search button */}
            <div className="flex items-center space-x-4">
              <div className="relative w-96">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Tìm kiếm tiêu chuẩn"
                  className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <button className="bg-green-600 text-white px-3 py-2 rounded text-sm font-medium hover:bg-green-700 transition-colors">
                Tìm kiếm
              </button>
            </div>

            {/* Row 2 - Filters */}
            <div className="flex space-x-4">
              <select className="border border-gray-300 rounded-md px-2 py-2 text-sm bg-white">
                <option>- Chọn đơn vị -</option>
              </select>
              <select className="border border-gray-300 rounded-md px-2 py-2 text-sm bg-white">
                <option>- Loại tiêu chuẩn -</option>
              </select>
              <select className="border border-gray-300 rounded-md px-2 py-2 text-sm bg-white">
                <option>- Cơ quan ban hành -</option>
              </select>
            </div>

            {/* Row 3 - Export button */}
            <div className="flex justify-start">
              <button className="bg-orange-500 text-white px-3 py-2 rounded text-sm font-medium hover:bg-orange-600 transition-colors flex items-center space-x-2">
                <FileDown className="h-4 w-4" />
                <span>Xuất ra file</span>
              </button>
            </div>
          </div>
        </div>

        {/* Data Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-8">
                    TT
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-48">
                    Tên đề tiêu chuẩn
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-32">
                    Đơn vị
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-40">
                    Nhiệm vụ KH&CN
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-32">
                    Ký hiệu
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-40">
                    Cơ quan ban hành
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-24">
                    Thời gian
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider w-20">
                    Chức năng
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {Object.entries(groupedData).map(([category, items]) => (
                  <React.Fragment key={category}>
                    {/* Category Header Row */}
                    <tr className="bg-gray-100">
                      <td className="px-2 py-3 text-sm font-bold text-gray-900 border-r border-gray-200 w-8 text-center">
                        {category === "Tiêu chuẩn quốc gia TCVN/QS"
                          ? "I"
                          : "II"}
                      </td>
                      <td
                        colSpan={7}
                        className="px-2 py-3 text-sm font-bold text-gray-900 border-r border-gray-200 text-center"
                      >
                        {category}
                      </td>
                    </tr>
                    {/* Data Rows for this category */}
                    {items.map((item) => (
                      <tr key={item.id} className="hover:bg-gray-50">
                        <td className="px-2 py-4 whitespace-nowrap text-sm font-medium text-gray-900 border-r border-gray-200 w-8">
                          {item.stt}
                        </td>
                        <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-48">
                          <div className="font-medium text-[#337ab7] text-[14px] leading-tight">
                            {item.standardName} »
                          </div>
                        </td>
                        <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-32">
                          {item.unit || ""}
                        </td>
                        <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-40">
                          {item.scienceTask || ""}
                        </td>
                        <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-32">
                          {item.symbol || ""}
                        </td>
                        <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-40">
                          {item.issuingAuthority || ""}
                        </td>
                        <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-24">
                          {item.time || ""}
                        </td>
                        <td className="px-2 py-4 whitespace-nowrap w-20">
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              aria-label="Sửa"
                              className="p-1 rounded text-[#337ab7] hover:bg-blue-50 hover:text-[#2e6da4] transition-colors"
                              onClick={() => {
                                navigate(`/edit-standard/${item.id}`);
                              }}
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              aria-label="Xóa"
                              className="p-1 rounded text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors"
                              onClick={() => {
                                setStandardToDelete(item);
                                setShowDeleteConfirm(true);
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination inside the table container */}
          <div className="flex justify-between items-center p-4 border-t border-gray-200">
            <div className="text-sm font-medium text-gray-700">
              Hiển thị <span className="font-bold">1</span> đến{" "}
              <select className="font-bold text-blue-600 border-none bg-transparent focus:ring-0 focus:outline-none text-sm">
                <option>10</option>
                <option>20</option>
                <option>50</option>
                <option>100</option>
              </select>{" "}
              trong <span className="font-bold">10000</span> kết quả
            </div>
            <div className="flex space-x-2">
              <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                &lt;
              </button>
              <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                1
              </button>
              <button className="px-3 py-2 text-sm font-medium text-white bg-[#337ab7] border border-[#337ab7] rounded-md">
                2
              </button>
              <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                3
              </button>
              <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                &gt;
              </button>
            </div>
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center">
            <div className="bg-white rounded-md p-4 max-w-sm w-full mx-4">
              <h3 className="text-base font-semibold text-gray-900 mb-2">
                Xác nhận xóa
              </h3>
              <p className="text-xs text-gray-600 mb-3">
                Bạn có chắc muốn xóa tiêu chuẩn{" "}
                <span className="font-semibold text-gray-900">
                  {standardToDelete?.standardName}
                </span>
                ? Hành động này không thể hoàn tác.
              </p>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setStandardToDelete(null);
                  }}
                  className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
                >
                  Hủy
                </button>
                <button
                  onClick={() => {
                    if (standardToDelete) {
                      // Here you would implement the actual delete logic
                      console.log("Deleting standard:", standardToDelete.id);
                    }
                    setShowDeleteConfirm(false);
                    setStandardToDelete(null);
                  }}
                  className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded hover:bg-red-700"
                >
                  Xóa
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </PageShell>
  );
};

export default Standards;
